import { Component, Emit, Vue } from 'nuxt-property-decorator';
import { saveAs } from 'file-saver';
import { gql } from 'graphql-tag';
import { cloneDeep } from 'lodash';
import getOrdersDoc from '../get-orders.gql';
import SubscriptionConfirmOrderModalComponent from './confirm-order-modal/index.vue';
import { OrderInformation } from './confirm-order-modal/index';
import SubscriptionOrderFilterModalComponent from './filter-modal/index.vue';
import OrderConfirmButtonComponent from '~/components/order/confirm-button/index.vue';
import {
  PurchaseOrder,
  PurchaseOrderQueriesFindAllArgs,
  Query
} from '~/schema';
import { OrderState } from '~/store/order';

/**
 * OrderSubscriptionTableItem interface defines the structure of each item in the subscription orders table
 */
interface OrderSubscriptionTableItem {
  id: string;
  code: string;
  buyerName: string;
  paymentStatus: string;
  subscriptionName: string;
  subscriptionType: string;
  kitcodes: Array<string>;
  sendKit: boolean;
  source: string;
  totalPrice: number;
  createdAt: string;
  purchaseType: string;
  paymentDate: string;
  subsStartDate: string;
  subsEndDate: string;
  buyer: PurchaseOrder['profile'] | undefined;
  shipment: PurchaseOrder['shipment'] | undefined;
  status: string;
}

/**
 * OrderSubscriptionTableItemKey enum defines the keys used for sorting and identifying columns in the subscription orders table
 */
export enum OrderSubscriptionTableItemKey {
  Id = 'id',
  Code = 'code',
  BuyerName = 'buyerName',
  PaymentStatus = 'paymentStatus',
  SubscriptionName = 'subscriptionName',
  SubscriptionType = 'subscriptionType',
  Kitcodes = 'kitcodes',
  SendKit = 'sendKit',
  Source = 'source',
  TotalPrice = 'totalPrice',
  CreatedAt = 'createdAt',
  PurchaseType = 'purchaseType',
  PaymentDate = 'paymentDate',
  SubsStartDate = 'subsStartDate',
  SubsEndDate = 'subsEndDate',
  Buyer = 'buyer',
  Shipment = 'shipment'
}

/**
 * Order Subscription Component
 *
 * @export
 * @class OrderSubscriptionComponent
 * @extends {Vue}
 */
@Component({
  components: {
    OrderConfirmButtonComponent,
    SubscriptionConfirmOrderModalComponent,
    SubscriptionOrderFilterModalComponent
  }
})
export default class OrderSubscriptionComponent extends Vue {
  protected aclEditor = false;
  protected tableHeaders: Array<any> = [
    {
      text: 'Order Code',
      sortable: false,
      align: 'start',
      value: OrderSubscriptionTableItemKey.Code,
      width: '130px'
    },
    {
      text: 'Buyer Name',
      sortable: false,
      align: 'start',
      value: OrderSubscriptionTableItemKey.BuyerName,
      width: '130px'
    },
    {
      text: 'Payment Status',
      sortable: false,
      align: 'start',
      value: OrderSubscriptionTableItemKey.PaymentStatus,
      width: '100px'
    },
    {
      text: 'Subscription Name',
      sortable: false,
      align: 'start',
      value: OrderSubscriptionTableItemKey.SubscriptionName,
      width: '130px'
    },
    {
      text: 'Subscription Type',
      sortable: false,
      align: 'start',
      value: OrderSubscriptionTableItemKey.SubscriptionType,
      width: '130px'
    },
    {
      text: 'Kitcode',
      sortable: false,
      align: 'start',
      value: OrderSubscriptionTableItemKey.Kitcodes,
      width: '130px'
    },
    {
      text: 'Send Kit',
      sortable: false,
      align: 'start',
      value: OrderSubscriptionTableItemKey.SendKit,
      width: '100px'
    },
    {
      text: 'Source',
      sortable: false,
      align: 'start',
      value: OrderSubscriptionTableItemKey.Source,
      width: '130px'
    },
    {
      text: 'Total Price',
      sortable: false,
      align: 'start',
      value: OrderSubscriptionTableItemKey.TotalPrice,
      width: '130px'
    },
    {
      text: 'Order Date',
      sortable: true,
      align: 'start',
      value: OrderSubscriptionTableItemKey.CreatedAt,
      width: '130px'
    },
    {
      text: 'Payment Date',
      sortable: false,
      align: 'start',
      value: OrderSubscriptionTableItemKey.PaymentDate,
      width: '130px'
    },
    {
      text: 'Subs Start Date',
      sortable: true,
      align: 'start',
      value: OrderSubscriptionTableItemKey.SubsStartDate,
      width: '130px'
    },
    {
      text: 'Subs End Date',
      sortable: true,
      align: 'start',
      value: OrderSubscriptionTableItemKey.SubsEndDate,
      width: '130px'
    },
    {
      text: '',
      align: 'end',
      value: 'actions',
      sortable: false
    }
  ];

  protected tableOptions: OrderState['subscription']['table']['options'] =
    this.$store.state.order.subscription.table.options;

  protected tableFooter: OrderState['subscription']['table']['footer'] =
    this.$store.state.order.subscription.table.footer;

  private refetchDataTimeout: NodeJS.Timeout | null = null;
  private handleSearchKeywordChangeDebounceTimeout: NodeJS.Timeout | null =
    null;

  protected tableItems: Array<OrderSubscriptionTableItem> = [];

  protected loading = {
    fetchOrders: false,
    exportExcel: false
  };

  protected confirmOrderModalIsOpen: boolean = false;
  protected filterModalIsOpen: boolean = false;

  protected selectedOrder: OrderInformation = {
    id: '',
    name: '',
    phone: '',
    email: '',
    address: '',
    province: '',
    city: '',
    district: '',
    postcode: ''
  };

  protected filter: {
    keyword: undefined | string;
  } = {
    keyword: undefined
  };

  protected serverOrderCount: number = 0;
  protected subscriptions: Array<() => any> = [];

  public mounted(): void {
    this.aclEditor = this.$editor();
    this.tableOptions = this.$store.state.order.subscription.table.options;
    const subscription = this.$store.subscribe((mutation, state) => {
      if (mutation.type === 'order/changeSubscriptionTableOptions') {
        this.tableOptions = state.order.subscription.table.options;
      }
      if (mutation.type === 'order/changeSubscriptionTableFilter') {
        this.filter.keyword = state.order.subscription.table.filter.keyword;
      }
    });
    this.subscriptions.push(subscription);
    this.refetch();
  }

  public beforeDestroy(): void {
    this.subscriptions.forEach((unsubscribe) => unsubscribe());
  }

  @Emit('click:view')
  protected viewItem(item: OrderSubscriptionTableItem): { id: string } {
    return {
      id: item.id
    };
  }

  protected async fetchOrders(): Promise<void> {
    try {
      this.loading.fetchOrders = true;
      const query = cloneDeep(this.$store.state.order.subscription.query);
      const variables: PurchaseOrderQueriesFindAllArgs = query;
      const request = await this.$api.query<Query>({
        query: getOrdersDoc,
        variables
      });
      const response = request.data?.Purchase?.Order?.findAll;
      this.tableItems = this.mapOrderToTableItem(response?.results ?? []);
      this.serverOrderCount = response?.count ?? 0;
    } catch (error) {
      this.$emitter.emit('TOAST', 'Failed to fetch subscription orders');
    } finally {
      this.loading.fetchOrders = false;
    }
  }

  /**
   * Refetch subscription orders data with a debounce to prevent excessive API calls when multiple changes occur in quick succession
   */
  public refetch(): void {
    if (this.refetchDataTimeout) {
      clearTimeout(this.refetchDataTimeout);
      this.refetchDataTimeout = null;
    }

    this.loading.fetchOrders = true;
    this.refetchDataTimeout = setTimeout(() => {
      this.tableItems = [];
      this.fetchOrders();
    }, 500);
  }

  protected async exportExcel() {
    this.loading.exportExcel = true;
    try {
      const mutation = gql`
        mutation exportSubscriptionOrders(
          $where: OrderQueryFilter
          $sort: OrderQuerySortBy
        ) {
          Purchase {
            Order {
              exportSubscription(where: $where, sort: $sort)
            }
          }
        }
      `;

      const res = await this.$api.mutate({
        mutation,
        variables: {
          ...this.$store.state.order.subscription.query,
          where: {
            ...this.$store.state.order.subscription.query?.where
          }
        }
      });
      const pointer = res?.data?.Purchase?.Order?.exportSubscription;
      const url = `${this.$config.CDN_URL}/` + pointer;
      const date = new Date().toISOString().substring(0, 10).replace(/-/g, '');
      saveAs(url, `${date} - Export Order (Subscription).xlsx`);
      this.remove(url);
    } catch (error) {
      this.$emitter.emit('TOAST', error);
    } finally {
      this.loading.exportExcel = false;
    }
  }

  async remove(url: string) {
    try {
      await fetch(url, {
        headers: {
          Authorization: this.$auth.getToken('local') || ''
        },
        method: 'DELETE'
      });
    } catch (error) {
      throw new Error(error);
    }
  }

  protected handleSearchKeywordChange(value: string): void {
    if (this.handleSearchKeywordChangeDebounceTimeout) {
      clearTimeout(this.handleSearchKeywordChangeDebounceTimeout);
      this.handleSearchKeywordChangeDebounceTimeout = null;
    }
    this.handleSearchKeywordChangeDebounceTimeout = setTimeout(() => {
      this.$store.commit('order/changeSubscriptionTableFilter', {
        ...this.$store.state.order.subscription.table.filter,
        keyword:
          typeof value === 'string' && value.trim() !== '' ? value : undefined
      });
      this.refetch();
    }, 500);
  }

  /**
   * Map raw purchase order data to the structure required for displaying in the subscription orders table
   * @param {Array<PurchaseOrder>} orders - The raw purchase order data to map
   * @returns {Array<OrderSubscriptionTableItem>} - The mapped table items ready for display
   */
  protected mapOrderToTableItem(
    orders: Array<PurchaseOrder>
  ): Array<OrderSubscriptionTableItem> {
    return orders.map((order) => ({
      id: order.id,
      code: order.code,
      purchaseType: order.purchaseType,
      buyerName: order.profile?.name ?? '',
      paymentStatus: order.status,
      subscriptionName: order.subscription?.subscriptionName ?? '',
      subscriptionType: order.subscription?.type ?? '',
      kitcodes: Array.isArray(order.subscription?.userKitcodes)
        ? order.subscription.userKitcodes
        : [],
      status: order.status,
      sendKit: order.subscription?.includeKit === true,
      source: order.subscription?.source ?? '',
      totalPrice: order.total ?? 0,
      createdAt: (order.createdAt as string) ?? '',
      paymentDate: order.payment?.paidAt ?? '',
      subsStartDate:
        (order.subscription?.subscriptionStartDate as string) ?? '',
      subsEndDate: (order.subscription?.subscriptionEndDate as string) ?? '',
      buyer: order.profile ?? undefined,
      shipment: order.shipment ?? undefined
    }));
  }

  protected openConfirmOrderModal(item?: OrderSubscriptionTableItem): void {
    this.selectedOrder = {
      id: item.id,
      name: item.buyerName,
      phone: item.shipment?.phone ?? '',
      email: item.shipment?.email ?? '',
      address: item.shipment?.address ?? '',
      province: item.shipment?.province ?? '',
      city: item.shipment?.city ?? '',
      district: item.shipment?.district ?? '',
      postcode: item.shipment?.postcode ?? ''
    };
    this.confirmOrderModalIsOpen = true;
  }

  protected updateTableOptions(options: any): void {
    this.$store.commit('order/changeSubscriptionTableOptions', options);
    this.tableItems = [];
    this.refetch();
  }

  protected onOrderConfirmed(): void {
    this.confirmOrderModalIsOpen = false;
    this.refetch();
  }
}
